import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
  permissions?: string[];
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  roles = [],
  permissions = [],
  redirectTo = '/login',
}) => {
  const { user, loading, isAuthenticated, checkRole, checkPermission } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth status
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="h6" color="text.secondary">
          Yetkilendirme kontrol ediliyor...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role-based access
  if (roles.length > 0) {
    const hasRequiredRole = roles.some((role) => checkRole(role));
    if (!hasRequiredRole) {
      return (
        <Navigate
          to="/unauthorized"
          state={{
            message: 'Bu sayfaya erişim yetkiniz bulunmamaktadır.',
            requiredRoles: roles,
          }}
          replace
        />
      );
    }
  }

  // Check permission-based access
  if (permissions.length > 0) {
    const hasRequiredPermission = permissions.some((permission) =>
      checkPermission(permission)
    );
    if (!hasRequiredPermission) {
      return (
        <Navigate
          to="/unauthorized"
          state={{
            message: 'Bu işlem için gerekli izniniz bulunmamaktadır.',
            requiredPermissions: permissions,
          }}
          replace
        />
      );
    }
  }

  // User is authenticated and authorized
  return <>{children}</>;
};

// HOC for protecting components
export const withProtectedRoute = <P extends object>(
  Component: React.ComponentType<P>,
  options?: {
    roles?: string[];
    permissions?: string[];
    redirectTo?: string;
  }
): React.FC<P> => {
  const ProtectedComponent: React.FC<P> = (props) => (
    <ProtectedRoute {...options}>
      <Component {...props} />
    </ProtectedRoute>
  );

  ProtectedComponent.displayName = `Protected(${Component.displayName || Component.name})`;

  return ProtectedComponent;
};

// Role-specific protected route components
export const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute roles={['admin']}>{children}</ProtectedRoute>
);

export const DoctorRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute roles={['doctor', 'admin']}>{children}</ProtectedRoute>
);

export const AssistantRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute roles={['assistant', 'admin']}>{children}</ProtectedRoute>
);

// Multi-role protected route
export const AuthorizedRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles?: string[];
}> = ({ children, allowedRoles = [] }) => (
  <ProtectedRoute roles={allowedRoles}>{children}</ProtectedRoute>
);

export default ProtectedRoute;